import {cloneDeep} from 'lodash';
import {IFlowlineJson} from '../../diagram-data/diagram-json-handler.service';

declare let mxGeometry: any; // available globally
declare let mxUtils: any; // available globally
declare let mxConstants: any; // available globally
declare let mxPoint: any; // available globally
declare let mxEvent: any; // available globally
declare let mxEventObject: any;
import {ShapeCommand} from '../commands.template';
import {setShapeValue} from '../../diagram-render/helpers/set-shape.util';
import {KEYS_TO_SANITIZE} from '../../shared/constants/keys-to-sanitize.constants';

interface ISimpleResizeAction {
  activity: any;
  yDiff: number;
  isSameCoord: boolean;
  isSameWidth: boolean;
  isSameHeight: boolean;
}

export class ChangeCommand extends ShapeCommand {
  protected firstAction: boolean;
  protected services: any; // see ShapeCommand class which is extended by command
  changes: any[] = [];
  reverseChanges: any[] = [];
  selfConnectedFlowlines: any[] = [];
  simpleResizeAction: ISimpleResizeAction;
  hasOverlayActions = false;
  overlayActions = [];
  _forceModelUpdate = false;
  constructor(data) {
    super(data);
    this.changes = data;
    this.firstAction = true;
  }

  prepareData() {
    this.simpleResizeAction = this.checkForSimpleBoxResizeAction();

    if (!this.simpleResizeAction?.isSameHeight && this.simpleResizeAction?.isSameWidth) {
      this.selfConnectedFlowlines = this.getSelfConnectedFlowlines(
        this.simpleResizeAction.activity.mxKey
      );
    }

    this.changes.forEach(change => {
      this.services.sanitizeXssService.sanitizeXss(change.data, KEYS_TO_SANITIZE.ATTACHMENT);
      const reverseChange: any = {};

      reverseChange.mxKey = change.mxKey;

      if (change.type === 'activity') {
        const activity = this.services.jsonHandlerSvc.get()['activities'].find(a => {
          return Number(a.mxKey) === Number(change.mxKey);
        });

        if (!activity) {
          return;
        }

        reverseChange.type = 'activity';
        reverseChange.action = 'changeActivity';
        reverseChange.data = {
          mxKey: change.mxKey,
          position: activity.position,
          text: activity.text,
          footerText: activity.footerText,
          locked: activity.locked
        };

        if (activity.image) {
          reverseChange.data.image = activity.image;
        }

        if (activity.imageMode) {
          reverseChange.data.imageMode = activity.imageMode;
        }

        if (activity.imageUrl) {
          reverseChange.data.imageUrl = activity.imageUrl;
        }
      } else if (change.type === 'text') {
        const text = this.services.jsonHandlerSvc.get()['texts'].find(t => {
          return Number(t.mxKey) === Number(change.mxKey);
        });

        if (!text) {
          return;
        }

        reverseChange.type = 'text';
        reverseChange.action = 'changeText';
        reverseChange.data = {
          mxKey: change.mxKey,
          position: text.position,
          text: text.text,
          locked: text.locked
        };
      } else if (change.type === 'connector') {
        const flowlineData = this.services.jsonHandlerSvc.get()['flowlines'].find(f => {
          return Number(f.mxKey) === Number(change.data.flowlineMxKey);
        });
        const connector = flowlineData.connector;

        reverseChange.type = 'connector';
        reverseChange.action = 'changeConnector';
        reverseChange.data = {
          flowlineMxKey: flowlineData.mxKey,
          position: connector.position,
          styleObject: connector.styleObject,
          customStyle: connector.customStyle
        };
      } else if (change.type === 'terminator') {
        const flowlineData = this.services.jsonHandlerSvc.get()['flowlines'].find(f => {
          return Number(f.mxKey) === Number(change.data.flowlineMxKey);
        });
        const terminator = flowlineData.terminator;

        reverseChange.type = 'terminator';
        reverseChange.action = 'changeTerminator';
        reverseChange.data = {
          flowlineMxKey: flowlineData.mxKey,
          position: terminator.position,
          styleObject: terminator.styleObject,
          customStyle: terminator.customStyle
        };
      } else if (change.type === 'sticker') {
        const sticker = this.services.jsonHandlerSvc.get()['notes'].find(n => {
          return Number(n.mxKey) === Number(change.mxKey);
        });

        if (!sticker) {
          return;
        }

        reverseChange.type = 'sticker';
        reverseChange.action = 'changeNote';
        reverseChange.data = {
          mxKey: Number(change.mxKey),
          position: sticker.position,
          text: sticker.text,
          paperClass: sticker.paperClass,
          locked: sticker.locked
        };
      } else if (change.type === 'image') {
        const image = this.services.jsonHandlerSvc.get()['images'].find(i => {
          return Number(i.mxKey) === Number(change.mxKey);
        });

        if (!image) {
          return;
        }

        reverseChange.type = 'image';
        reverseChange.action = 'changeImage';
        reverseChange.data = {
          mxKey: Number(change.mxKey),
          position: image.position,
          locked: image.locked
        };
      } else if (change.type === 'flowline') {
        const flowline: IFlowlineJson = this.services.jsonHandlerSvc.get()['flowlines'].find(f => {
          return Number(f.mxKey) === Number(change.mxKey);
        });

        if (!flowline) {
          return;
        }

        if (this.isSelfConnected(flowline) && this.isFlowlineSide(flowline)) {
          if (change.data.waypoints[0]) {
            change.data.waypoints[0].y += Math.round(
              this.simpleResizeAction.yDiff * flowline.position.connectionPoints.exitY
            );
          }

          this.hasOverlayActions = true;
          this.overlayActions.push(cloneDeep(change));
        }

        reverseChange.type = 'flowline';
        reverseChange.action = 'changeFlowline';
        reverseChange.data = {
          mxKey: Number(change.mxKey),
          position: flowline.position,
          waypoints: flowline.waypoints,
          text: flowline.text,
          srcType: flowline.srcType,
          tgtType: flowline.tgtType
        };

        if (flowline.srcPosition) {
          reverseChange.data.srcPosition = flowline.srcPosition;
        }

        if (flowline.tgtPosition) {
          reverseChange.data.tgtPosition = flowline.tgtPosition;
        }

        if (flowline.srcType !== 'none') {
          reverseChange.data.srcId = flowline.srcId;
        }

        if (flowline.tgtType !== 'none') {
          reverseChange.data.tgtId = flowline.tgtId;
        }
      } else if (change.type === 'lineText') {
        const flowlineData = this.services.jsonHandlerSvc.get()['flowlines'].find(f => {
          return Number(f.mxKey) === Number(change.data.flowlineMxKey);
        });
        const lineTextData = flowlineData.lineTexts.find(item => item.key === change.data.key);

        reverseChange.type = 'lineText';
        reverseChange.action = change.action;
        reverseChange.data = {
          flowlineMxKey: flowlineData.mxKey,
          key: lineTextData.key,
          position: lineTextData.position,
          text: lineTextData.text
        };
      } else if (change.type === 'interaction') {
        const interaction = this.services.jsonHandlerSvc.get().interactions.find(ia => {
          return Number(ia.flowlineMxKey) === Number(change.data.flowlineMxKey);
        });

        reverseChange.type = 'interaction';
        reverseChange.action = change.action;
        reverseChange.data = {
          flowlineMxKey: interaction.flowlineMxKey,
          offset: interaction.offset
        };
      }

      this.reverseChanges.push(reverseChange);
    });

    return this;
  }

  forceModelUpdate(force = false) {
    this._forceModelUpdate = force;

    return this;
  }

  execute() {
    super.execute();
    const graph = this.services.registrySvc.getGraph();
    const model = graph.getModel();
    const groupUpdate = this.services.diagramUpdateSvc.group();

    model.beginUpdate();
    this.changes.forEach(change => {
      const action: any = {};

      action[change.action] = change.data;
      groupUpdate.add(action);

      if (!this.firstAction || this._forceModelUpdate) {
        this.makeChanges(change, model);
      }
    });
    this.firstAction = false;
    model.endUpdate();
    graph.fireEvent(new mxEventObject(mxEvent.CHANGE_COMMAND));

    return groupUpdate.sendGroup();
  }

  reverse() {
    super.reverse();
    const graph = this.services.registrySvc.getGraph();
    const model = graph.getModel();
    const groupUpdate = this.services.diagramUpdateSvc.group();

    model.beginUpdate();
    this.reverseChanges.forEach(change => {
      const action: any = {};

      action[change.action] = change.data;
      groupUpdate.add(action);
      this.makeChanges(change, model);
    });
    model.endUpdate();

    return groupUpdate.sendGroup();
  }

  makeChanges(change, model) {
    const cell = model.getCell(change.mxKey);

    if (!cell) {
      return;
    }

    cell.skipUndo = true;

    if (change.type === 'activity') {
      const geo = new mxGeometry(
        change.data.position.x,
        change.data.position.y,
        change.data.position.w,
        change.data.position.h
      );

      geo.relative = false;
      model.setGeometry(cell, geo);
      model.setStyle(
        cell,
        mxUtils.setStyle(cell.style, mxConstants.STYLE_LOCKED, +!!change.data.locked)
      );
      cell.comp.instance.textSub.next(change.data.text);
      const newValue = setShapeValue(cell, change.data.text);

      model.setValue(cell, newValue);
    }

    if (change.type === 'text') {
      const geo = new mxGeometry(
        change.data.position.x,
        change.data.position.y,
        change.data.position.w,
        change.data.position.h
      );

      const newValue = setShapeValue(cell, change.data.text);

      model.setValue(cell, newValue);
      model.setGeometry(cell, geo);
      model.setStyle(cell, mxUtils.setStyle(cell.style, 'rotation', change.data.position.rotation));
      model.setStyle(
        cell,
        mxUtils.setStyle(cell.style, mxConstants.STYLE_LOCKED, +!!change.data.locked)
      );
    }

    if (change.type === 'connector' || change.type === 'terminator') {
      const geo = new mxGeometry(
        change.data.position.x,
        change.data.position.y,
        change.data.position.w,
        change.data.position.h
      );

      geo.relative = false;
      model.setGeometry(cell, geo);
    }

    if (change.type === 'sticker') {
      const geo = new mxGeometry(
        change.data.position.x,
        change.data.position.y,
        change.data.position.w,
        change.data.position.h
      );

      geo.relative = false;

      const newValue = setShapeValue(cell, change.data.text);

      model.setStyle(cell, mxUtils.setStyle(cell.style, 'rotation', change.data.position.rotation));
      model.setStyle(cell, mxUtils.setStyle(cell.style, 'paperClass', change.data.paperClass));
      model.setStyle(
        cell,
        mxUtils.setStyle(cell.style, mxConstants.STYLE_LOCKED, +!!change.data.locked)
      );
      model.setValue(cell, newValue);
      model.setGeometry(cell, geo);
    }

    if (change.type === 'image') {
      const geo = new mxGeometry(
        change.data.position.x,
        change.data.position.y,
        change.data.position.w,
        change.data.position.h
      );

      model.setStyle(cell, mxUtils.setStyle(cell.style, 'rotation', change.data.position.r));
      model.setStyle(
        cell,
        mxUtils.setStyle(cell.style, mxConstants.STYLE_LOCKED, +!!change.data.locked)
      );
      model.setGeometry(cell, geo);
    }

    if (change.type === 'flowline') {
      const graph = this.services.registrySvc.getGraph();
      const geo = new mxGeometry(+change.data.position.x, +change.data.position.y);

      geo.relative = true;
      geo.offset = new mxPoint(+change.data.position.offset.x, +change.data.position.offset.y);

      if (change.data.srcPosition) {
        const pt = new mxPoint(change.data.srcPosition.x, change.data.srcPosition.y);

        geo.setTerminalPoint(pt, true);
        model.setTerminal(cell, null, true);
      }

      if (change.data.tgtPosition) {
        const pt = new mxPoint(change.data.tgtPosition.x, change.data.tgtPosition.y);

        geo.setTerminalPoint(pt, false);
        model.setTerminal(cell, null, false);
      }

      if (change.data.srcId) {
        const srcCell = model.getCell(Number(change.data.srcId));

        model.setTerminal(cell, srcCell, true);
        graph.setCellStyles('exitX', change.data.position.connectionPoints.exitX, [cell]);
        graph.setCellStyles('exitY', change.data.position.connectionPoints.exitY, [cell]);
      }

      if (change.data.tgtId) {
        const tgtCell = model.getCell(Number(change.data.tgtId));

        model.setTerminal(cell, tgtCell, false);
        graph.setCellStyles('entryX', change.data.position.connectionPoints.entryX, [cell]);
        graph.setCellStyles('entryY', change.data.position.connectionPoints.entryY, [cell]);
      }

      if (change.data.waypoints) {
        geo.points = [];

        for (let i = 0; i < change.data.waypoints.length; i++) {
          const pt = change.data.waypoints[i];

          geo.points.push(new mxPoint(pt.x, pt.y));
        }
      }

      const newValue = setShapeValue(cell, change.data.text);

      model.setValue(cell, newValue);

      model.setGeometry(cell, geo);
    }

    if (change.type === 'lineText') {
      const geo = new mxGeometry(change.data.position.x, change.data.position.y);

      geo.offset = new mxPoint(change.data.position.offset.x, change.data.position.offset.y);
      geo.relative = true;

      const newValue = setShapeValue(cell, change.data.text);

      model.setValue(cell, newValue);
      model.setGeometry(cell, geo);
    }

    if (change.type === 'interaction') {
      const geo = cell.geometry.clone();

      geo.offset = new mxPoint(change.data.offset.x, change.data.offset.y);
      geo.relative = true;

      model.setGeometry(cell, geo);
    }
  }

  checkForSimpleBoxResizeAction(): ISimpleResizeAction {
    interface IPosition {
      h: number;
      w: number;
      x: number;
      y: number;
    }
    const activityChanges = this.findByType('activity');

    if (activityChanges?.length !== 1) {
      return null;
    }

    const [change] = activityChanges;

    const activity = this.services.jsonHandlerSvc.get()['activities'].find(a => {
      return Number(a.mxKey) === Number(change.mxKey);
    });

    if (!activity) {
      return null;
    }

    const oldPosition: IPosition = activity.position;
    const newPosition: IPosition = change.data.position;

    const isSameCoord = oldPosition.x === newPosition.x && oldPosition.y === newPosition.y;
    const isSameWidth = oldPosition.w === newPosition.w;
    const isSameHeight = oldPosition.h === newPosition.h;
    const yDiff = newPosition.h - oldPosition.h;

    return {isSameCoord, isSameWidth, isSameHeight, activity, yDiff};
  }

  getSelfConnectedFlowlines(activityMX) {
    const flowlineChanges = this.findByType('flowline');

    return flowlineChanges.filter(change => {
      const {srcId, tgtId} = this.getFlowlineByMX(change.mxKey) || {};

      return Number(srcId) === Number(activityMX) && Number(tgtId) === Number(activityMX);
    });
  }

  findByType(type: string): any[] {
    return this.changes.filter(change => change.type === type);
  }

  getFlowlineByMX(mxKey): IFlowlineJson {
    return this.services.jsonHandlerSvc.get()['flowlines'].find(flowline => {
      return Number(flowline.mxKey) === Number(mxKey);
    });
  }

  isSelfConnected(flowline: IFlowlineJson): boolean {
    return !!this.selfConnectedFlowlines.find(sCF => Number(sCF.mxKey) === Number(flowline.mxKey));
  }

  isFlowlineSide(flowline: IFlowlineJson): boolean {
    const isSideX =
      Number(flowline.position.connectionPoints.exitX) <= 0.01 ||
      Number(flowline.position.connectionPoints.exitX) >= 0.99;
    const isSideY =
      Number(flowline.position.connectionPoints.exitY) >= 0.01 ||
      Number(flowline.position.connectionPoints.exitY) <= 0.99;

    return isSideY && isSideX;
  }
}
